
import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, Grid, InputLabel, MenuItem, Paper, Radio, RadioGroup, Select, Switch, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography } from '@mui/material';

import moment from 'moment';

import { openSnackbar } from '../store/slices/SnackbarSlice';

import { usePages } from '../hooks/usePages';

import { TablePaginationActions } from '../helpers/TablePaginationActions';
import { TablePaddingStyle, TableTheadStyle } from '../styles/TableStyle';

import { AgregarCitacionSentencia, CambiarEstatusCitacionSentencia, ObtenerAcuerdosCitacionSentencia, ObtenerAudienciasCitacionSentencia, ObtenerCitacionSentenciaExpediente } from '../connections/sentencia/CitacionSentenciaConnection';

import { ExpedienteProps } from '../interfaces/tribunal/ExpedienteVirtualInterface';
import { AcuerdoCitacionSentenciaProps, AudienciaCitacionSentenciaProps, CitacionSentenciaProps } from '../interfaces/sentencia/CitacionSentenciaInterface';

interface Props {
    openDialog:         boolean;
    handleCloseDialog:  () => void;
    expediente:         ExpedienteProps;
}

export const DialogAgregarCitacionSentencia = ( { openDialog, handleCloseDialog, expediente }: Props ) => {

    const dispatch = useDispatch();

    const [tipoCitacion, setTipoCitacion] = useState( 1 );
    const [tipoSentencia, setTipoSentencia] = useState( 1 );

    const [acuerdo, setAcuerdo] = useState( 0 );
    const [acuerdoArray, setAcuerdoArray] = useState<AcuerdoCitacionSentenciaProps[]>( [] );

    const [audiencia, setAudiencia] = useState( 0 );
    const [audienciaArray, setAudienciaArray] = useState<AudienciaCitacionSentenciaProps[]>( [] );

    const [rows, setRows] = useState<CitacionSentenciaProps[]>( [] );

    const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePages();

    const [loading, setLoading] = useState( true );
    const [loadingGuardar, setLoadingGuardar] = useState( false );

    const puedeGuardar = ( acuerdo !== 0 || audiencia !== 0 ) && tipoSentencia !== 0;

    const handleAgregar = async () => {

        setLoadingGuardar( true );

        await AgregarCitacionSentencia({
            idExpediente:       expediente.idExpediente,
            idAcuerdo:          acuerdo,
            idAudiencia:        audiencia,
            idTipoSentencia:    tipoSentencia,
        }).then( resp => {

            setTimeout(() => {

                const { success, message, idCitacionSentencia, fecha, idAcuerdo, idTipoAcuerdo, tipoAcuerdo, idAudiencia, idTipoAudiencia, tipoAudiencia, idEstatus, estatus, numeroExpediente, idTipoSentencia, tipoSentencia: tipoSentenciaDescripcion } = resp;

                if( success ){

                    setRows( prev => ([
                        ...prev,
                        {
                            idCitacionSentencia,
                            fecha,
                            idAcuerdo,
                            idTipoAcuerdo,
                            tipoAcuerdo,
                            idAudiencia,
                            idTipoAudiencia,
                            tipoAudiencia,
                            idEstatus,
                            estatus,
                            numeroExpediente,
                            idTipoSentencia,
                            tipoSentencia: tipoSentenciaDescripcion,
                        },
                    ]));

                    setAcuerdo( 0 );
                    setAudiencia( 0 );

                    dispatch( openSnackbar({ message, variant: 'success' }) );

                }
                else {

                    dispatch( openSnackbar({ message, variant: 'error' }) );

                }

                setLoadingGuardar( false );

            }, 700);

        });

    }

    const handleChangeEstatus = async ( idCitacionSentencia: number, checked: boolean ) => {

        await CambiarEstatusCitacionSentencia({
            idCitacionSentencia,
            value: checked,
        }).then( resp => {

            const { success, message } = resp;

            if( success ){

                setRows( prev => prev.map( (row) =>
                    row.idCitacionSentencia === idCitacionSentencia
                    ? { ...row, idEstatus: checked ? 5 : 2, estatus: checked ? 'Vigente' : 'Cancelada' }
                    : row
                ));

            }
            else {

                dispatch( openSnackbar({ message, variant: 'error' }) );

            }

        });

    }

    const handleCerrarDialog = () => {

        setTipoCitacion( 1 );
        setTipoSentencia( 1 );
        setAcuerdo( 0 );
        setAudiencia( 0 );
        setRows( [] );

        handleCloseDialog();

    }

    useEffect(() => {

        async function Obtener(){

            setAcuerdo( 0 );
            setAcuerdoArray( [] );

            await ObtenerAcuerdosCitacionSentencia( expediente.idExpediente ).then( resp => {
                setAcuerdoArray( resp );
            });

        }

        if( openDialog ){
            Obtener();
        }

    }, [ expediente.idExpediente, openDialog ]);

    useEffect(() => {

        async function Obtener(){

            setAudiencia( 0 );
            setAudienciaArray( [] );

            await ObtenerAudienciasCitacionSentencia( expediente.idExpediente ).then( resp => {
                setAudienciaArray( resp );
            });

        }

        if( openDialog ){
            Obtener();
        }

    }, [ expediente.idExpediente, openDialog ]);

    useEffect(() => {

        async function Obtener(){

            setRows( [] );
            setLoading( true );

            await ObtenerCitacionSentenciaExpediente( expediente.idExpediente ).then( resp => {

                setTimeout(() => {

                    setRows( resp );
                    setLoading( false );

                }, 500);

            });

        }

        if( openDialog ){
            Obtener();
        }

    }, [ expediente.idExpediente, openDialog ]);

    return (

        <Dialog
            fullWidth
            maxWidth='md'
            open={ openDialog }
            onClose={ () => {} }
        >

            <DialogTitle>Agregar citación de sentencia</DialogTitle>

            <DialogContent>

                <Grid container spacing={3} sx={{ mt: 1 }}>

                    <Grid size={{ md: 12, xs: 12 }}>

                        <FormControl component='fieldset'>
                            <FormLabel sx={{ color: 'black' }}>Tipo de citación:</FormLabel>
                            <RadioGroup row>
                                <FormControlLabel value={1} control={ <Radio checked={ tipoCitacion === 1 } onChange={ () => { setTipoCitacion( 1 ); setAcuerdo( 0 ); setAudiencia( 0 ); } } /> } label='Acuerdo(s)' />
                                <FormControlLabel value={2} control={ <Radio checked={ tipoCitacion === 2 } onChange={ () => { setTipoCitacion( 2 ); setAcuerdo( 0 ); setAudiencia( 0 ); } } /> } label='Audiencia(s)' />
                            </RadioGroup>
                        </FormControl>

                    </Grid>

                    <Grid size={{ md: 12, xs: 12 }}>

                        <FormControl fullWidth>

                            <InputLabel shrink>Acuerdo(s)</InputLabel>

                            <Select
                                notched
                                label='Acuerdo(s)'
                                value={ acuerdo }
                                onChange={ (e) => setAcuerdo( e.target.value as number ) }
                                disabled={ tipoCitacion !== 1 }                             
                            >
                                <MenuItem value={0}>Selecciona una opción</MenuItem>
                                {
                                    acuerdoArray.map( ({ idAcuerdo, tipoAcuerdo, fecha }) => (
                                        <MenuItem key={ idAcuerdo } value={ idAcuerdo }>

                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>

                                                <Typography variant='body1'><strong>GAC-{ idAcuerdo }</strong></Typography>
                                                <Typography variant='caption' color='text.secondary'>
                                                    Tipo de acuerdo: <strong>{ tipoAcuerdo }</strong> | Fecha: <strong>{ fecha ? moment( fecha ).format('DD-MM-YYYY HH:mm a') : '' }</strong>
                                                </Typography>

                                            </Box>

                                        </MenuItem>
                                    ))
                                }
                            </Select>

                        </FormControl>

                    </Grid>

                    <Grid size={{ md: 12, xs: 12 }}>

                        <FormControl fullWidth>

                            <InputLabel shrink>Audiencia(s)</InputLabel>

                            <Select
                                notched
                                label='Audiencia(s)'
                                value={ audiencia }
                                onChange={ (e) => setAudiencia( e.target.value as number ) }
                                disabled={ tipoCitacion !== 2 }
                            >
                                <MenuItem value={0}>Selecciona una opción</MenuItem>
                                {
                                    audienciaArray.map( ({ idAudiencia, tipoAudiencia, fecha }) => (
                                        <MenuItem key={ idAudiencia } value={ idAudiencia }>

                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>

                                                <Typography variant='body1'><strong>GAU-{ idAudiencia }</strong></Typography>
                                                <Typography variant='caption' color='text.secondary'>
                                                    Tipo de audiencia: <strong>{ tipoAudiencia }</strong> | Fecha: <strong>{ fecha ? moment( fecha ).format('DD-MM-YYYY HH:mm a') : '' }</strong>
                                                </Typography>

                                            </Box>

                                        </MenuItem>
                                    ))
                                }
                            </Select>

                        </FormControl>

                    </Grid>

                    <Grid size={{ md: 6, xs: 12 }}>

                        <FormControl component='fieldset'>
                            <FormLabel sx={{ color: 'black' }}>Seleccione el tipo de sentencia:</FormLabel>
                            <RadioGroup row>
                                <FormControlLabel value={1} control={ <Radio checked={ tipoSentencia === 1 } onChange={ () => setTipoSentencia( 1 ) } /> } label='Sentencia Definitiva' />
                                <FormControlLabel value={2} control={ <Radio checked={ tipoSentencia === 2 } onChange={ () => setTipoSentencia( 2 ) } /> } label='Sentencia Interlocutoria' />
                            </RadioGroup>
                        </FormControl>

                    </Grid>

                    <Grid size={{ md: 6, xs: 12 }} sx={{ textAlign: 'right' }}>

                        <Button
                            variant='contained'
                            loading={ loadingGuardar }
                            disabled={ !puedeGuardar }
                            onClick={ handleAgregar }
                        >
                            Agregar
                        </Button>

                    </Grid>

                    <Grid size={{ md: 12, xs: 12 }}>

                        <TableContainer sx={{ borderRadius: 3 }} component={Paper}>

                            <Table>

                                <TableHead sx={{ ...TableTheadStyle }}>

                                    <TableRow>
                                        <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '25%', fontWeight: 'bold' }}>Número de seguimiento</TableCell>
                                        <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '20%', fontWeight: 'bold' }}>Tipo de sentencia</TableCell>
                                        <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '25%', fontWeight: 'bold' }}>Origen</TableCell>
                                        <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '10%', fontWeight: 'bold' }}></TableCell>
                                    </TableRow>

                                </TableHead>

                                <TableBody>

                                    {
                                        (
                                            rowsPerPage > 0
                                            ? 
                                                rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            : 
                                                rows
                                        ).map( ( { idCitacionSentencia, idAudiencia, idAcuerdo, fecha, tipoSentencia, tipoAudiencia, tipoAcuerdo, idEstatus, estatus
                                         }, index) => (

                                            <TableRow key={ idCitacionSentencia } sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>

                                                <TableCell>

                                                    {
                                                        ( idAcuerdo !== 0 || idAudiencia !== 0 )
                                                        &&
                                                            <Typography variant='subtitle1' fontWeight={'bold'} color={'primary'}>
                                                                { idAudiencia !== 0 ? `GAU-${ idAudiencia }` : `GAC-${ idAcuerdo }` }
                                                            </Typography>
                                                    }

                                                    <Typography fontSize={( idAcuerdo !== 0 || idAudiencia !== 0 ) ? 14 : undefined } variant={ ( idAcuerdo !== 0 || idAudiencia !== 0 ) ? 'caption' : 'subtitle1' } fontWeight={ ( idAcuerdo !== 0 || idAudiencia !== 0 ) ? 'normal' : 'bold'} color={ ( idAcuerdo !== 0 || idAudiencia !== 0 ) ? 'text.secondary' : 'primary' }>
                                                        { fecha ? moment( fecha ).format( ( idAcuerdo === 0 && idAudiencia === 0 ) ? 'DD-MM-YY' : 'DD-MM-YY HH:mm a') : '' }
                                                    </Typography>

                                                </TableCell>

                                                <TableCell>
                                                    
                                                    <Typography variant='body2'>
                                                        { tipoSentencia }
                                                    </Typography>

                                                </TableCell>

                                                <TableCell>

                                                    <Typography variant='body2'>
                                                        { idAudiencia !== 0 && 'Citada en audiencia:' }
                                                        { idAcuerdo !== 0 && 'Citada en acuerdo:' }
                                                        { ( idAcuerdo === 0 && idAudiencia === 0 ) && 'Citada por sistema' }
                                                    </Typography>

                                                    {
                                                        ( idAudiencia !== 0 || idAcuerdo !== 0 )
                                                        &&
                                                            <Typography fontSize={14} variant='caption' color='text.secondary'>
                                                                ({ idAudiencia !== 0 ? tipoAudiencia : tipoAcuerdo })
                                                            </Typography>
                                                    }

                                                </TableCell>

                                                <TableCell>

                                                    {
                                                        idEstatus !== 6
                                                        &&
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>

                                                                <Switch
                                                                    checked={ idEstatus === 5 }
                                                                    onChange={ (e) => handleChangeEstatus( idCitacionSentencia, e.target.checked ) }
                                                                />

                                                            </Box>
                                                    }

                                                    {
                                                        idEstatus === 6
                                                        &&
                                                            <Chip 
                                                                color='primary'
                                                                label={
                                                                    <Typography variant='caption' fontWeight='bold'>
                                                                        { estatus }
                                                                    </Typography>   
                                                                }
                                                            />                                                          
                                                    }

                                                </TableCell>

                                            </TableRow>

                                        ))
                                    }

                                    { 
                                        ( rows.length === 0 && !loading )
                                        ?   
                                            <TableRow style={{ height: 73 }}>

                                                <TableCell colSpan={7} align='center'> 

                                                    <Typography variant='caption' color='secondary'> No se encontraron resultados </Typography>
                                                    
                                                </TableCell>

                                            </TableRow>                     
                                        :
                                            loading
                                            &&
                                                <TableRow style={{ height: 73 }}>

                                                    <TableCell colSpan={7} align='center'> 

                                                        <CircularProgress size={26} />
                                                        
                                                    </TableCell>

                                                </TableRow>                     
                                    }

                                </TableBody>

                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            rowsPerPageOptions={[ ]}
                                            colSpan={5}
                                            count={ rows.length }
                                            rowsPerPage={ rowsPerPage }
                                            page={ page }
                                            onPageChange={ handleChangePage }
                                            onRowsPerPageChange={ handleChangeRowsPerPage }
                                            ActionsComponent={ TablePaginationActions }
                                            labelDisplayedRows={
                                                ({ from, to, count }) => {
                                                    return '' + from + '-' + to + ' de ' + count
                                                }
                                            }
                                        />
                                    </TableRow>
                                </TableFooter>

                            </Table>

                        </TableContainer>

                    </Grid>

                </Grid>

            </DialogContent>

            <DialogActions>
                <Button color='secondary' disabled={ loadingGuardar } onClick={ handleCerrarDialog }>Cerrar</Button>
            </DialogActions>

        </Dialog>

    );

}
